import { useCallback, useEffect, useState } from 'react';
import { App, Button, Form, Input, Modal, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  MinusCircleFilled,
  PlusOutlined,
} from '@ant-design/icons';
import { listSpokes, provisionSpoke } from '@/api/tenantAdmin';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { Spoke } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { formatAbsolute } from '@/lib/time';

const { Title, Paragraph, Text } = Typography;

function SpokeStatusTag({ status }: { status: string }) {
  const s = status?.toUpperCase?.() ?? '';
  let color = 'var(--ol-info)';
  let Icon = MinusCircleFilled;
  if (s === 'ACTIVE' || s === 'ENABLED' || s === 'READY') {
    color = 'var(--ol-positive)';
    Icon = CheckCircleFilled;
  } else if (s === 'PROVISIONING' || s === 'PENDING') {
    color = 'var(--ol-info)';
    Icon = LoadingOutlined;
  } else if (s === 'SUSPENDED' || s === 'DISABLED') {
    color = 'var(--ol-attention)';
    Icon = ExclamationCircleFilled;
  } else if (s === 'ERROR' || s === 'FAILED') {
    color = 'var(--ol-negative)';
    Icon = ExclamationCircleFilled;
  }
  return (
    <span
      title={status}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color, fontSize: 12, fontWeight: 600 }}
    >
      <Icon aria-hidden />
      {status}
    </span>
  );
}

interface ProvisionForm {
  spokeId: string;
  displayName?: string;
  regime?: string;
}

/** B1 · F-TA-04 — spoke inventory + self-serve provisioning. */
export function SpokesPanel() {
  const { message } = App.useApp();
  const [spokes, setSpokes] = useState<Spoke[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<ProvisionForm>();

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      const res = await listSpokes();
      setSpokes(res.spokes);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
      setSpokes([]);
    } finally {
      setLoading(false);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(async () => {
    let values: ProvisionForm;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const res = await provisionSpoke({
        spokeId: values.spokeId.trim(),
        displayName: values.displayName?.trim() || undefined,
        regime: values.regime?.trim() || undefined,
      });
      message.success(`Spoke “${res.spokeId}” — ${res.status}`);
      setModalOpen(false);
      form.resetFields();
      void load();
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.code === 'PROVISIONING_UNAVAILABLE' || n.httpStatus === 503) {
        message.warning(
          'Spoke provisioning is temporarily unavailable (the provisioning saga is not accepting requests right now). Your request was not applied — try again shortly.',
        );
      } else if (isNotAvailable(n)) {
        message.info('The spoke-provisioning endpoint is not live yet (Track A pending).');
      } else {
        message.error(n.message);
      }
    } finally {
      setSubmitting(false);
    }
  }, [form, load, message]);

  const columns: ColumnsType<Spoke> = [
    {
      title: 'Spoke',
      dataIndex: 'spokeId',
      key: 'spokeId',
      render: (_, r) => (
        <div style={{ minWidth: 160 }}>
          <div style={{ fontWeight: 600 }}>{r.displayName ?? r.spokeId}</div>
          {r.displayName && (
            <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
              {r.spokeId}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (s: string) => <SpokeStatusTag status={s} />,
    },
    {
      title: 'Regime',
      dataIndex: 'regime',
      key: 'regime',
      width: 140,
      render: (r?: string) => r ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'Entities',
      dataIndex: 'entityCount',
      key: 'entityCount',
      width: 100,
      align: 'right',
      render: (n?: number) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{(n ?? 0).toLocaleString()}</span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatAbsolute(v)}
        </Text>
      ),
    },
  ];

  const provisionButton = (
    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
      Provision spoke
    </Button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <Title level={5} style={{ margin: 0 }}>
            Spokes
          </Title>
          <Paragraph type="secondary" style={{ margin: '4px 0 0', maxWidth: 680, fontSize: 13 }}>
            The delivery tenants this hub oversees. Provision a spoke to onboard a new operating unit —
            it inherits the hub regime unless you set its own.
          </Paragraph>
        </div>
        {!unavailable && provisionButton}
      </div>

      {unavailable ? (
        <EmptyState
          icon={<PlusOutlined />}
          title="Spoke administration"
          description="Your spokes and self-serve provisioning land here once the tenant-admin API is live (Track A, PS-4)."
          bullets={[
            'A spoke inventory with status, regime and entity counts',
            'Self-serve provisioning (spokeId + display name + regime)',
            'Honest handling when the provisioning saga is unavailable (503)',
          ]}
          action={provisionButton}
        />
      ) : (
        <Table<Spoke>
          rowKey="spokeId"
          loading={loading}
          columns={columns}
          dataSource={spokes}
          size="small"
          pagination={false}
          locale={{ emptyText: 'No spokes yet. Provision the first one.' }}
        />
      )}

      <Modal
        title="Provision a spoke"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText="Provision"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Paragraph type="secondary" style={{ fontSize: 12.5 }}>
          The spoke id is permanent and scopes its realm. Use a short, stable slug (lowercase, hyphenated).
        </Paragraph>
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            name="spokeId"
            label="Spoke id"
            rules={[
              { required: true, message: 'A spoke id is required.' },
              {
                pattern: /^[a-z0-9][a-z0-9-]{1,38}$/,
                message: 'Lowercase letters, digits and hyphens; 2–39 chars.',
              },
            ]}
          >
            <Input placeholder="e.g. mumbai-ops" autoFocus />
          </Form.Item>
          <Form.Item name="displayName" label="Display name">
            <Input placeholder="e.g. Mumbai Operations" />
          </Form.Item>
          <Form.Item name="regime" label="Regime" tooltip="Leave blank to inherit the hub regime.">
            <Input placeholder="e.g. IN-RBI" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
